import React, { useState, useEffect } from 'react';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { DocumentTextIcon, DocumentIcon, ArchiveBoxIcon } from '@heroicons/react/24/solid';
import BottomNav from '../components/mobile/BottomNav';
import { useTranslations } from '../hooks/useTranslations';
import pdf1 from '../assets/other/009 CommonExcelWindowsShortcuts-01.pdf';
import pptx1 from '../assets/other/001 EXCEL-FUNCTIONS.pptx';
import xlsx1 from '../assets/other/007 OrderOfOperation-01.xlsx';
import pdf2 from '../assets/other/1.1 Gameplan.pdf';
import pdf3 from '../assets/other/2.1 Strategy2 Pair Trading.pdf';
import pdf4 from '../assets/other/3.1 Hedging.pdf';
import pdf5 from '../assets/other/4.1 Cointegration.pdf';
import pdf6 from '../assets/other/5.1 Trading Technique.pdf';

function getFileIcon(ext) {
  switch (ext.toLowerCase()) {
    case 'pdf':
      return <DocumentTextIcon className="w-14 h-14 text-red-500 mx-auto" />;
    case 'xlsx':
    case 'xls':
      return <DocumentTextIcon className="w-14 h-14 text-green-500 mx-auto" />;
    case 'docx':
    case 'doc':
      return <DocumentTextIcon className="w-14 h-14 text-blue-500 mx-auto" />;
    case 'zip':
    case 'rar':
      return <ArchiveBoxIcon className="w-14 h-14 text-yellow-500 mx-auto" />;
    default:
      return <DocumentIcon className="w-14 h-14 text-gray-400 mx-auto" />;
  }
}

function OtherMaterial() {
  const { t } = useTranslations();
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const materials = [
    {
      id: 1,
      title: t('otherMaterial.materials.excelShortcuts.title'),
      file: pdf1,
      ext: 'pdf',
      description: t('otherMaterial.materials.excelShortcuts.description')
    },
    {
      id: 2,
      title: t('otherMaterial.materials.excelFunctions.title'),
      file: pptx1,
      ext: 'pptx',
    },
    {
      id: 3,
      title: t('otherMaterial.materials.orderOfOperation.title'),
      file: xlsx1,
      ext: 'xlsx',
    },
    {
      id: 4,
      title: t('otherMaterial.materials.gameplan.title'),
      file: pdf2,
      ext: 'pdf',
    },
    {
      id: 5,
      title: t('otherMaterial.materials.strategy2PairTrading.title'),
      file: pdf3,
      ext: 'pdf',
    },
    {
      id: 6,
      title: t('otherMaterial.materials.hedging.title'),
      file: pdf4,
      ext: 'pdf',
    },
    {
      id: 7,
      title: t('otherMaterial.materials.cointegration.title'),
      file: pdf5,
      ext: 'pdf',
    },
    {
      id: 8,
      title: t('otherMaterial.materials.tradingTechnique.title'),
      file: pdf6,
      ext: 'pdf',
    },
    // Добавляй новые материалы сюда
  ];

  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsLoading(false);
        return;
      }
      try {
        const response = await fetch("https://api.maxprofitacademy.com/user", {
          method: "GET",
          headers: { "token": JSON.parse(token) }
        });
        const data = await response.json();
        if (data.status === 200) {
          setUserData(data.data);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserData();
  }, []);

  const hasValidCourse = userData?.course && ['GOLD', 'SILVER', 'PLATINUM'].includes(userData.course.toUpperCase());

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        <span className="ml-3 text-gray-600">{t('otherMaterial.loading')}</span>
      </div>
    );
  }
  
  if (!hasValidCourse) {
    return (
      <div className="flex flex-col items-center justify-center">
        <div className="bg-primary/10 mt-10 p-6 rounded-2xl mb-6 flex items-center justify-center">
          <DocumentTextIcon className="w-12 h-12 text-primary mr-4" />
          <span className="text-xl font-semibold text-gray-700">{t('otherMaterial.noAccessTitle')}</span>
        </div>
        <BottomNav />
      </div>
    );
  }

  const handleOpenFile = (file) => {
    window.open(file, '_blank');
  };

  return (
    <div className="min-h-screen py-10">
      
      {materials.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20">
          <DocumentIcon className="w-16 h-16 text-gray-300 mb-4" />
          <div className="text-gray-500 text-lg">{t('otherMaterial.noMaterials')}</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 pb-20">
          {materials.map((mat) => (
            <div
              key={mat.id}
              className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 flex flex-col items-center p-7 group border border-transparent hover:border-primary"
            >
              <div className="mb-4">{getFileIcon(mat.ext)}</div>
              <div className="text-center w-full flex-1 flex flex-col">
                <h3 className="text-lg font-bold mb-1 text-gray-900 truncate">{mat.title}</h3>
                <div className="text-xs text-gray-400 mb-2">.{mat.ext.toUpperCase()}</div>
                {mat.description && <div className="text-gray-500 text-sm mb-4">{mat.description}</div>}
                {hasValidCourse ? (
                  <button
                    onClick={() => handleOpenFile(mat.file)}
                    className="inline-flex items-center justify-center bg-gradient-to-r from-primary to-primary/80 text-black font-semibold px-6 py-2 rounded-xl shadow hover:from-primary/90 hover:to-primary/90 transition-colors text-base mt-auto"
                  >
                    <DocumentTextIcon className="w-5 h-5 mr-2 text-black" />
                    {t('otherMaterial.open')}
                  </button>
                ) : (
                  <button
                    className="inline-flex items-center justify-center bg-gray-100 text-gray-400 px-6 py-2 rounded-xl font-semibold cursor-not-allowed mt-auto"
                    disabled
                  >
                    <LockClosedIcon className="w-5 h-5 mr-2" />
                    {t('otherMaterial.unavailable')}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <BottomNav />
    </div>
  );
}

export default OtherMaterial; 